import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Lock, Calendar, CheckCircle2, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

function fmtWt(v) { return v != null ? Number(v).toFixed(1) : '-'; }
function fmtKg(v) { return v != null ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v) : '-'; }

export default function SWMErectionProgress({ projectId, filters }) {
  const [tagRows, setTagRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTag, setEditingTag] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(
        `/v1/projects/${projectId}/swm/erection/tags${filters?.area ? `?area=${filters.area}` : ''}`
      );
      setTagRows(r.data?.tags || []);
    } catch (e) { console.error('SWM erection fetch failed', e); }
    setLoading(false);
  }, [projectId, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTagClick = (tag) => {
    if (tag.erectionGate !== 'READY') return;
    setEditingTag(tag.structuralTag);
    setEditDate(new Date().toISOString().slice(0, 10));
  };

  const handleSaveErection = async () => {
    if (!editingTag || !editDate) return;
    setSaving(true);
    try {
      const r = await api.put(
        `/v1/projects/${projectId}/swm/tag-erection`,
        { date: editDate },
        { params: { structuralTag: editingTag } }
      );
      toast.success(`Tag ${editingTag} erected (${r.data?.marksErected || 0} marks rolled up)`);
      setEditingTag(null);
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message || detail.error : detail;
      toast.error(msg || 'Erection blocked by gate enforcement');
    }
    setSaving(false);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;

  const renderTagErectionGate = (tag) => {
    const isEditing = editingTag === tag.structuralTag;

    if (isEditing) {
      return (
        <div className="flex items-center gap-1" data-testid={`tag-erection-edit-${tag.structuralTag}`}>
          <input type="date" value={editDate} onChange={e => setEditDate(e.target.value)}
            className="text-[9px] px-1 py-0.5 rounded border w-24"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid={`tag-erection-date-input-${tag.structuralTag}`} />
          <button onClick={handleSaveErection} disabled={saving} className="p-0.5 rounded" style={{ color: '#22c55e' }}
            data-testid={`tag-erection-save-${tag.structuralTag}`}>
            <CheckCircle2 className="w-3 h-3" />
          </button>
          <button onClick={() => setEditingTag(null)} className="p-0.5 rounded" style={{ color: '#ef4444' }}
            data-testid={`tag-erection-cancel-${tag.structuralTag}`}>
            <X className="w-3 h-3" />
          </button>
        </div>
      );
    }

    if (tag.erectionGate === 'ERECTED') {
      const d = tag.erectionDate;
      return (
        <span className="text-[9px] font-mono font-bold" style={{ color: '#22c55e' }}
          data-testid={`tag-erection-complete-${tag.structuralTag}`}>
          {typeof d === 'string' ? d.slice(0, 10) : 'Erected'}
        </span>
      );
    }

    if (tag.erectionGate === 'LOCKED') {
      return (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center gap-0.5 cursor-not-allowed opacity-50"
                data-testid={`tag-erection-locked-${tag.structuralTag}`}>
                <Lock className="w-3 h-3" style={{ color: '#ef4444' }} />
                <span className="text-[8px]" style={{ color: '#ef4444' }}>Locked</span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-[10px] max-w-[220px]">
              <p className="font-semibold text-red-400">Tag Erection Blocked</p>
              <p>{tag.lockReason || 'Not all marks dispatched'}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button onClick={() => handleTagClick(tag)}
              className="flex items-center justify-center gap-0.5 cursor-pointer hover:opacity-70"
              data-testid={`tag-erection-ready-${tag.structuralTag}`}>
              <Calendar className="w-3 h-3" style={{ color: '#3b82f6' }} />
              <span className="text-[8px]" style={{ color: '#3b82f6' }}>Set Date</span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-[10px]">All marks dispatched · click to record tag erection date</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const totalWt = tagRows.reduce((s, t) => s + (t.totalWeight || 0), 0);
  const erectedWt = tagRows.reduce((s, t) => s + (t.erectedWeight || 0), 0);
  const totalTags = tagRows.length;
  const erectedTags = tagRows.filter(t => t.erectionGate === 'ERECTED').length;
  const wtPct = totalWt > 0 ? (erectedWt / totalWt * 100) : 0;

  return (
    <div className="space-y-3" data-testid="swm-erection-progress">
      {/* Tag Erection summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}
          data-testid="erection-tag-card">
          <p className="text-[10px] font-medium mb-1" style={{ color: '#22c55e' }}>Tag Erection</p>
          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{wtPct.toFixed(1)}%</p>
          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {fmtKg(erectedWt)} / {fmtKg(totalWt)} kg
          </p>
          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
            {erectedTags} / {totalTags} tags
          </p>
        </div>
      </div>

      {/* Gate Legend */}
      <div className="flex items-center gap-3 p-2 rounded-lg" style={{ background: 'var(--card-bg-alt)', border: '1px solid var(--border-color)' }}
        data-testid="erection-gate-legend">
        <span className="text-[9px] font-medium" style={{ color: 'var(--text-muted)' }}>Gate Legend:</span>
        <span className="flex items-center gap-1 text-[9px]"><Lock className="w-3 h-3" style={{ color: '#ef4444' }} /> Blocked (not all marks dispatched)</span>
        <span className="flex items-center gap-1 text-[9px]"><Calendar className="w-3 h-3" style={{ color: '#3b82f6' }} /> Ready for erection</span>
        <span className="flex items-center gap-1 text-[9px]"><CheckCircle2 className="w-3 h-3" style={{ color: '#22c55e' }} /> Erected</span>
      </div>

      {/* Tag-level table — erection happens at tag level */}
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-[10px]" data-testid="erection-tag-table">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Structural Tag</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Area</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Section</th>
              <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>MTO Wt (kg)</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Marks</th>
              <th className="p-2 text-center font-medium" style={{ color: '#3b82f6' }}>Dispatched</th>
              <th className="p-2 text-right font-medium" style={{ color: 'var(--text-muted)' }}>Wt Total</th>
              <th className="p-2 text-right font-medium" style={{ color: '#22c55e' }}>Wt Erected</th>
              <th className="p-2 text-center font-medium" style={{ color: '#22c55e', borderLeft: '2px solid var(--border-color)' }}>Erection Gate</th>
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {tagRows.map(tag => (
              <tr key={tag.structuralTag} style={{ borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)' }} data-testid={`erec-tag-${tag.structuralTag}`}>
                <td className="p-2 font-mono font-semibold" style={{ color: 'var(--accent-primary)' }}>{tag.structuralTag}</td>
                <td className="p-2" style={{ color: 'var(--text-muted)' }}>{tag.area || '-'}</td>
                <td className="p-2" style={{ color: 'var(--text-muted)' }}>{tag.section || '-'}</td>
                <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtKg(tag.totalMTOWeight)}</td>
                <td className="p-2 text-center" style={{ color: 'var(--text-primary)' }}>{tag.totalMarks}</td>
                <td className="p-2 text-center font-mono" style={{ color: tag.dispatchedMarks === tag.totalMarks && tag.totalMarks > 0 ? '#22c55e' : '#3b82f6' }}>
                  {tag.dispatchedMarks} / {tag.totalMarks}
                </td>
                <td className="p-2 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtWt(tag.totalWeight)}</td>
                <td className="p-2 text-right font-mono" style={{ color: '#22c55e' }}>{fmtWt(tag.erectedWeight)}</td>
                <td className="p-2 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}>
                  {renderTagErectionGate(tag)}
                </td>
                <td className="p-2 text-center text-[9px]" style={{ color: tag.erectionStatus === 'ERECTED' ? '#22c55e' : 'var(--text-muted)' }}>
                  {tag.erectionStatus}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
        {tagRows.length} tags
      </p>
    </div>
  );
}
